import React, { FormEvent, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Badge,
  Button,
  Card,
  ConfirmationDialog,
  DateInput,
  Label,
  Select,
  Switch,
  TextInput,
} from '../../components/ui';
import { PlusIcon, SpinnerIcon, TrashIcon, UserPlusIcon } from '../../components/ui/Icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { createApprovalDelegation, deleteApprovalDelegation, updateApprovalDelegation } from '../../services/mutations';
import { normalizePaginatedListResponse } from '../../services/paginatedList';
import { ApprovalDelegation, useApprovalDelegations } from './useData';

type User = { id: number; userDisplayName?: string; userFirstName: string; userLastName: string };

function asList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  return normalizePaginatedListResponse<T>(data as any, 1, 500).data;
}

function userLabel(
  user?: User | { id: number; userDisplayName?: string | null; userFirstName: string; userLastName: string } | null,
) {
  if (!user) return 'Unknown user';
  return user.userDisplayName || `${user.userFirstName} ${user.userLastName}`.trim();
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function isExpired(delegation: ApprovalDelegation) {
  return !!delegation.validTo && new Date(delegation.validTo).getTime() < Date.now();
}

export const Delegations: React.FC = () => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const { data: delegations = [], isLoading, mutate } = useApprovalDelegations();
  const { data: usersRaw } = useSWR('/users?limit=500', fetcher);
  const users = useMemo(() => asList<User>(usersRaw), [usersRaw]);

  const [delegateUserId, setDelegateUserId] = useState<number | ''>('');
  const [validTo, setValidTo] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ApprovalDelegation | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const myDelegations = useMemo(
    () => delegations.filter((d) => d.userId === user?.id),
    [delegations, user?.id],
  );
  const delegatedToMe = useMemo(
    () => delegations.filter((d) => d.delegateUserId === user?.id),
    [delegations, user?.id],
  );

  const userOptions = useMemo(
    () =>
      users
        .filter((u) => u.id !== user?.id)
        .map((u) => ({ value: u.id, label: userLabel(u) })),
    [users, user?.id],
  );

  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (!delegateUserId) return;
    setSaving(true);
    setError(null);
    try {
      await createApprovalDelegation({
        delegateUserId,
        validTo: validTo || undefined,
        note: note.trim() || undefined,
      });
      setDelegateUserId('');
      setValidTo('');
      setNote('');
      await mutate();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to create delegation';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (delegation: ApprovalDelegation) => {
    setBusyId(delegation.id);
    try {
      await updateApprovalDelegation(delegation.id, { isActive: !delegation.isActive });
      await mutate();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5 p-4 md:p-6">
      {error && (
        <Card className="border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </Card>
      )}

      <Card className="p-5">
        <h2 className={cn('mb-4 text-sm font-semibold', colors.text)}>New delegation</h2>
        <form onSubmit={create} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <Label required>Delegate to</Label>
            <Select
              required
              value={delegateUserId}
              onChange={(e) => setDelegateUserId(e.target.value ? Number(e.target.value) : '')}
              options={userOptions}
              placeholder="Select user"
            />
          </div>
          <div className="w-48">
            <Label>Until (optional)</Label>
            <DateInput value={validTo} onChange={(e) => setValidTo(e.target.value)} placeholder="End date" />
          </div>
          <div className="min-w-[200px] flex-1">
            <Label>Note (optional)</Label>
            <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. On leave" />
          </div>
          <Button type="submit" disabled={!delegateUserId} loading={saving}>
            <PlusIcon className="h-4 w-4" />
            Delegate
          </Button>
        </form>
      </Card>

      <Card className="overflow-hidden p-0">
        <div className={cn('border-b px-5 py-3.5', colors.border)}>
          <h2 className={cn('text-sm font-semibold', colors.text)}>My delegations</h2>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>Who can act on your behalf while you&apos;re away.</p>
        </div>
        {isLoading ? (
          <div className={cn('flex items-center justify-center gap-2 px-6 py-10 text-sm', colors.textMuted)}>
            <SpinnerIcon className="h-4 w-4" />
            Loading…
          </div>
        ) : myDelegations.length === 0 ? (
          <div className={cn('px-6 py-10 text-center text-sm', colors.textMuted)}>
            <UserPlusIcon className="mx-auto mb-2 h-6 w-6 opacity-60" />
            No delegations yet — create one above before you go away.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {myDelegations.map((d) => {
              const expired = isExpired(d);
              const active = d.isActive && !expired;
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('font-medium', colors.text)}>
                      {userLabel(d.delegateUser)}
                      {d.note ? <span className={cn('ml-2 text-xs font-normal', colors.textMuted)}>“{d.note}”</span> : null}
                    </p>
                    <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                      From {formatDate(d.validFrom) || '—'}
                      {d.validTo ? ` to ${formatDate(d.validTo)}` : ' · no end date'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={expired ? 'secondary' : active ? 'success' : 'secondary'}>
                      {expired ? 'Expired' : active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Switch
                      size="sm"
                      label={d.isActive ? 'Active' : 'Inactive'}
                      checked={d.isActive && !expired}
                      disabled={busyId === d.id || expired}
                      onChange={() => void toggleActive(d)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={() => setPendingDelete(d)}
                      aria-label="Delete delegation"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card className="overflow-hidden p-0">
        <div className={cn('border-b px-5 py-3.5', colors.border)}>
          <h2 className={cn('text-sm font-semibold', colors.text)}>Delegated to me</h2>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
            You can act on these colleagues&apos; pending approval steps while their delegation is active.
          </p>
        </div>
        {isLoading ? (
          <div className={cn('flex items-center justify-center gap-2 px-6 py-10 text-sm', colors.textMuted)}>
            <SpinnerIcon className="h-4 w-4" />
            Loading…
          </div>
        ) : delegatedToMe.length === 0 ? (
          <div className={cn('px-6 py-10 text-center text-sm', colors.textMuted)}>Nobody has delegated to you.</div>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {delegatedToMe.map((d) => {
              const expired = isExpired(d);
              const active = d.isActive && !expired;
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('font-medium', colors.text)}>{userLabel(d.user)}</p>
                    <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                      From {formatDate(d.validFrom) || '—'}
                      {d.validTo ? ` to ${formatDate(d.validTo)}` : ' · no end date'}
                    </p>
                  </div>
                  <Badge variant={expired ? 'secondary' : active ? 'success' : 'secondary'}>
                    {expired ? 'Expired' : active ? 'Active' : 'Inactive'}
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <ConfirmationDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          await deleteApprovalDelegation(pendingDelete.id);
          setPendingDelete(null);
          await mutate();
        }}
        title="Delete delegation"
        message={pendingDelete ? `Stop delegating to “${userLabel(pendingDelete.delegateUser)}”?` : undefined}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};
